/**
 * Authentication API Service
 * 
 * Handles all authentication-related API calls including login, logout,
 * registration, password reset, and user profile management.
 */

import { apiService } from './api';
import type { ApiResponse } from './api';
import type { User } from '../types';
import type { UserProfile, AppraisalCycle } from '../types/rbac';
import { ENDPOINTS } from '../config/apiEndpoints';

/**
 * OTP request interface
 */
export interface OtpRequest {
  email: string;
}


/**
 * OTP verification request interface
 */
export interface OtpVerificationRequest {
  email: string;
  otp: string;
}

/**
 * OTP verification response interface - This matches the actual API response structure
 */
export interface OtpVerificationResponse  {
    token: string;
    name: string;
    userName: string;
  
}

/**
 * Authentication service class
 */
class AuthService {

  /**
   * Send OTP to user's email
   * 
   * @param email - User's email address
   * @returns Promise with OTP response containing success message
   */
  async sendOtp(email: string): Promise<ApiResponse<Record<string, unknown>>> {
      const response = await apiService.post<Record<string, unknown>>('/main/get_otp/', { email });

      console.log('🔍 sendOtp response:', JSON.stringify(response, null, 2));
      
      return response;
  }

  /**
   * Verify OTP and authenticate user
   * 
   * @param email - User's email address
   * @param otp - OTP code received via email
   * @returns Promise with authentication response containing user data and token
   */
  async verifyOtp(email: string, otp: string): Promise<ApiResponse<OtpVerificationResponse>> {
   
      const response = await apiService.post<OtpVerificationResponse>('/main/verify_otp/', { 
        email, 
        otp 
      });
      
      console.log('🔍 verifyOtp response:', JSON.stringify(response, null, 2));
      
      // Store authentication token on successful verification
      // Check for successful status and proper response structure
      if (response.status && response.status_code === 200 && response.data && response.data.token) {
        console.log('✅ Token found, storing authentication data');
        apiService.setAuthToken(response.data.token);
        
        // Create a user object with data from the response
        const user = {
          id: '', // Will be populated when getCurrentUser() is called
          name: response.data.name,
          email: response.data.userName, // userName is the email
          designation: '',
          department: ''
        };
        localStorage.setItem('user', JSON.stringify(user));
        
        console.log('✅ User data stored successfully:', user);
      } else {
        console.warn('⚠️ Token not found in response or verification failed. Response details:', {
          status: response.status,
          statusCode: response.status_code,
          hasData: !!response.data,
          hasToken: !!(response.data && response.data.token),
          responseData: response.data
        });
      }
      
      return response;
      
   
  }

  /**
   * Logout user
   */
  /**
   * Logout user - clears authentication data locally and notifies server
   * 
   * @returns Promise with logout response
   */
  async logout(): Promise<ApiResponse<Record<string, unknown>>> {
    console.log('🔍 Initiating logout process...');
    
    try {
      // Attempt to notify server of logout
      const response = await apiService.put<Record<string, unknown>>('/api/v1/employee/sign_out/');
      console.log('✅ Server logout successful:', response);
      
      return {
        status: true,
        status_code: response.status_code || 200,
        message: response.message || 'Logged out successfully',
        error: '',
        data: response.data || { message: 'Logout successful' }
      };
      
    } catch (error) {
      console.error('❌ Server logout failed:', error);
      
      // Handle API errors gracefully
      if (error && typeof error === 'object' && 'response' in error) {
        const apiError = error as { response: { data: ApiResponse<Record<string, unknown>>; status: number } };
        console.warn('⚠️ Server logout error (continuing with local cleanup):', apiError.response);
        
        // Return the EXACT API response data - don't modify the message
        return apiError.response.data;
      }
      
      // Handle network/connection errors
      console.warn('⚠️ Network error during logout (continuing with local cleanup)');
      return {
        status: false,
        status_code: 503,
        message: 'Logged out locally (server unreachable)',
        error: error instanceof Error ? error.message : 'Network error',
        data: { message: 'Local logout completed despite network error' }
      };
      
    } finally {
      // Always clear local authentication data regardless of server response
      console.log('🧹 Clearing local authentication data...');
      this.clearAuth();
      console.log('✅ Logout process completed');
    }
  }

   /**
   * Get current user profile
   */
  async getCurrentUser(email: string): Promise<ApiResponse<User>> {
    try {
      console.log('🔍 Fetching current user profile...');
      const response = await apiService.get<User>('/api/v1/employee/profile/', {params: { email } });
      console.log('✅ User profile fetched successfully:', response);
      const user = response.data;
      // Update user in auth hook
      localStorage.setItem('user', JSON.stringify(user));

      return response;
    } catch (error) {
      console.error('❌ Failed to fetch user profile:', error);
      
      // Handle different types of errors
      if (error && typeof error === 'object' && 'response' in error) {
        const apiError = error as { response: { data: ApiResponse<User>; status: number } };
        console.error('User Profile API Error Response:', apiError.response);
        
        // Handle specific status codes
        if (apiError.response?.status === 401) {
          // Unauthorized - clear auth data
          console.warn('⚠️ Unauthorized access, clearing authentication data');
          this.clearAuth();
        }
        
        // Return the EXACT API response data - don't modify the message
        return apiError.response.data;
      }
      
      // Handle network errors
      return {
        status: false,
        status_code: 503,
        message: 'Unable to fetch user profile. Please check your connection.',
        error: error instanceof Error ? error.message : 'Network error',
        data: {} as User
      };
    }
  }

  /**
   * Get user profile with RBAC permissions
   */
  async getUserProfileWithPermissions(email: string): Promise<ApiResponse<UserProfile>> {
    try {
      console.log('🔍 Fetching user profile with RBAC permissions...');
      const response = await apiService.get<UserProfile>('/api/v1/employee/profile/', {params: { email } });
      console.log('✅ User profile with permissions fetched successfully:', response);
      
      // Store the full profile with permissions
      if (response.status && response.data) {
        localStorage.setItem('userProfile', JSON.stringify(response.data));
        // Support both 'access' (correct) and 'acesss' (legacy typo)
        const permissions = response.data.access || response.data.acesss;
        if (permissions) {
          localStorage.setItem('userPermissions', JSON.stringify(permissions));
        }
      }

      return response;
    } catch (error) {
      console.error('❌ Failed to fetch user profile with permissions:', error);
      
      if (error && typeof error === 'object' && 'response' in error) {
        const apiError = error as { response: { data: ApiResponse<UserProfile>; status: number } };
        console.error('Profile API Error Response:', apiError.response);
        
        if (apiError.response?.status === 401) {
          console.warn('⚠️ Unauthorized access, clearing authentication data');
          this.clearAuth();
        }
        
        return apiError.response.data;
      }
      
      return {
        status: false,
        status_code: 503,
        message: 'Unable to fetch user profile. Please check your connection.',
        error: error instanceof Error ? error.message : 'Network error',
        data: {} as UserProfile
      };
    }
  }

  /**
   * Get appraisal cycle information with deadlines
   */
  async getAppraisalCycle(email: string): Promise<ApiResponse<AppraisalCycle>> {
    try {
      console.log('🔍 Fetching appraisal cycle information...');
      const response = await apiService.get<AppraisalCycle>(ENDPOINTS.APPRAISAL_CYCLE, {params: { email } });
      console.log('✅ Appraisal cycle fetched successfully:', response);
      
      // Store the appraisal cycle data
      if (response.status && response.data) {
        localStorage.setItem('appraisalCycle', JSON.stringify(response.data));
      }

      return response;
    } catch (error) {
      console.error('❌ Failed to fetch appraisal cycle:', error);
      
      if (error && typeof error === 'object' && 'response' in error) {
        const apiError = error as { response: { data: ApiResponse<AppraisalCycle>; status: number } };
        console.error('Appraisal Cycle API Error Response:', apiError.response);
        
        return apiError.response.data;
      }
      
      return {
        status: false,
        status_code: 503,
        message: 'Unable to fetch appraisal cycle. Please check your connection.',
        error: error instanceof Error ? error.message : 'Network error',
        data: {} as AppraisalCycle
      };
    }
  }
 

  /**
   * Check if user is authenticated (client-side check)
   */
  isAuthenticated(): boolean {
    return apiService.isAuthenticated();
  }

  /**
   * Get stored user data
   */
  getStoredUser(): User | null {
    try {
      const userData = localStorage.getItem('user');
      if (!userData) {
        return null;
      }
      
      const user = JSON.parse(userData) as User;
      console.log('🔍 Retrieved stored user:', user);
      return user;
    } catch (error) {
      console.error('❌ Error parsing stored user data:', error);
      // Clear corrupted data
      localStorage.removeItem('user');
      return null;
    }
  }

  /**
   * Get stored refresh token
   */
  getStoredRefreshToken(): string | null {
    try {
      const refreshToken = localStorage.getItem('refreshToken');
      if (refreshToken) {
        console.log('🔍 Retrieved stored refresh token');
      }
      return refreshToken;
    } catch (error) {
      console.error('❌ Error retrieving refresh token:', error);
      return null;
    }
  }



  /**
   * Clear all authentication data
   */
  clearAuth(): void {
    try {
      console.log('🧹 Clearing all authentication data...');
      apiService.clearAuthToken();
      localStorage.clear();
      sessionStorage.clear();
      console.log('✅ Authentication data cleared successfully');
    } catch (error) {
      console.error('❌ Error clearing authentication data:', error);
      // Force clear by trying each item individually as fallback
      try {
        localStorage.removeItem('user');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('auth_user');
        localStorage.removeItem('authToken');
        localStorage.removeItem('userProfile');
        localStorage.removeItem('userPermissions');
        localStorage.removeItem('appraisalCycle');
        localStorage.removeItem('login_email');
        localStorage.removeItem('login_step');
        localStorage.removeItem('selfAppraisal_activeTab');
        localStorage.removeItem('selfAppraisal_immediateManager');
        localStorage.removeItem('selfAppraisal_nominationApprover');
        localStorage.removeItem('error_context');
        sessionStorage.clear();
      } catch (fallbackError) {
        console.error('❌ Critical error: Could not clear storage:', fallbackError);
      }
    }
  }
}

// Export singleton instance
export const authService = new AuthService();
export default authService;
